// Utils
import { formValidators } from "@benchmark/ui/utils";

// Types
import type { LocationFormData } from "@benchmark/core/types";

/**
 * Validates latitude is within -90 to 90.
 * @agentic @benchmark/core
 */
const validateLatitude = (value: number | undefined): string | null => {
  if (value === undefined) return null;
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "Latitude must be a number";
  }
  if (value < -90 || value > 90) {
    return "Latitude must be between -90 and 90";
  }
  return null;
};

/**
 * Validates longitude is within -180 to 180.
 * @agentic @benchmark/core
 */
const validateLongitude = (value: number | undefined): string | null => {
  if (value === undefined) return null;
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "Longitude must be a number";
  }
  if (value < -180 || value > 180) {
    return "Longitude must be between -180 and 180";
  }
  return null;
};

/**
 * Returns validation errors for location section form data.
 * @agentic @benchmark/core
 */
export const getLocationSectionErrors = (
  locations: LocationFormData[] | undefined,
  fieldPath: string,
): Record<string, string> => {
  if (!locations || locations.length === 0) {
    return {};
  }

  const errorEntries: Array<[string, string]> = [];

  locations.forEach((location, index) => {
    if (!location?.isNew) {
      return;
    }

    const locationPath = `${fieldPath}.${index}`;
    const nameError = formValidators.required("Location name")(location.name);
    const streetError = formValidators.required("Street address")(
      location.street,
    );
    const cityError = formValidators.required("City")(location.city);
    const stateError = formValidators.required("State")(location.state);
    const zipError = formValidators.zipCode(true)(location.zip);
    const latError = validateLatitude(location.latitude);
    const lngError = validateLongitude(location.longitude);

    if (nameError) {
      errorEntries.push([`${locationPath}.name`, nameError]);
    }
    if (streetError) {
      errorEntries.push([`${locationPath}.street`, streetError]);
    }
    if (cityError) {
      errorEntries.push([`${locationPath}.city`, cityError]);
    }
    if (stateError) {
      errorEntries.push([`${locationPath}.state`, stateError]);
    }
    if (zipError) {
      errorEntries.push([`${locationPath}.zip`, zipError]);
    }
    if (latError) {
      errorEntries.push([`${locationPath}.latitude`, latError]);
    }
    if (lngError) {
      errorEntries.push([`${locationPath}.longitude`, lngError]);
    }
  });

  return Object.fromEntries(errorEntries);
};
